import type { Vertical } from '@/types';

export const mockVerticals: Vertical[] = [
  {
    id: 'legal',
    name: 'Legal Services',
    duplicate_window_days: 90,
    requires_tcpa_consent: true,
    lifecycle_statuses: ['new','contacted','consultation_scheduled','retained','case_closed','lost'],
    intake_schema: {
      required: ['case_type','incident_date','tcpa_consent'],
      fields: {
        case_type: { type: 'enum', options: ['personal_injury','auto_accident','medical_malpractice','workers_comp','mass_tort','family_law','criminal','immigration','other'] },
        incident_date: { type: 'date' },
        incident_description: { type: 'text', max: 500 },
        injury_severity: { type: 'enum', options: ['minor','moderate','severe','catastrophic'] },
        represented: { type: 'boolean' },
        jurisdiction_state: { type: 'string' },
        case_value_estimate: { type: 'enum', options: ['lt_10k','10k_50k','50k_250k','gt_250k','unknown'] },
        tcpa_consent: { type: 'boolean' },
        tcpa_consent_text: { type: 'string' },
        tcpa_consent_at: { type: 'timestamp' }
      }
    },
    active: true,
    created_at: '2024-01-01T00:00:00Z'
  },
  {
    id: 'real_estate',
    name: 'Real Estate',
    duplicate_window_days: 30,
    requires_tcpa_consent: false,
    lifecycle_statuses: ['new','contacted','viewing_scheduled','offer_made','closed','lost'],
    intake_schema: {
      required: ['lead_type','price_range_min','timeline'],
      fields: {
        lead_type: { type: 'enum', options: ['buyer','seller','rental_tenant','rental_landlord'] },
        property_type: { type: 'enum', options: ['residential','commercial','land','multi_family'] },
        price_range_min: { type: 'number' },
        price_range_max: { type: 'number' },
        currency: { type: 'enum', options: ['EUR','USD'] },
        timeline: { type: 'enum', options: ['immediately','1_3_months','3_6_months','6_plus_months','just_browsing'] },
        pre_approved: { type: 'boolean' },
        num_bedrooms: { type: 'enum', options: ['1','2','3','4','5+'] },
        target_neighborhoods: { type: 'array' },
        motivation: { type: 'enum', options: ['upsizing','downsizing','relocation','investment','first_time'] },
        property_address: { type: 'string' }
      }
    },
    active: true,
    created_at: '2024-01-01T00:00:00Z'
  },
  {
    id: 'home_services',
    name: 'Home Services',
    duplicate_window_days: 60,
    requires_tcpa_consent: false,
    lifecycle_statuses: [
      'new',
      'contacted',
      'showroom_planned',
      'showroom_visited',
      'offerte_verstuurd',
      'offerte_herzien',
      'akkoord',
      'getekend',
      'aanbetaling_gevraagd',
      'aanbetaling_ontvangen',
      'werkbezoek_gepland',
      'werkbezoek_gedaan',
      'materiaal_besteld',
      'levering_gepland',
      'geleverd',
      'oplevering_verstuurd',
      'afgerond',
      'nabetaling_gevraagd',
      'nabetaling_ontvangen',
      'verloren',
    ],
    intake_schema: {
      required: ['service_type','project_size','timeline_urgency'],
      fields: {
        service_type: { type: 'enum', options: ['renovation','painting','solar','roofing','hvac','plumbing','electrical','landscaping','cleaning','other'] },
        service_subtype: { type: 'string' },
        property_type: { type: 'enum', options: ['residential_owned','residential_rented','commercial'] },
        project_size: { type: 'enum', options: ['small_lt_2k','medium_2k_10k','large_10k_50k','major_gt_50k'] },
        timeline_urgency: { type: 'enum', options: ['emergency','within_1_month','1_3_months','planning_ahead'] },
        budget_range: { type: 'string' },
        property_age_years: { type: 'number' }
      }
    },
    active: true,
    created_at: '2024-01-01T00:00:00Z'
  },
  {
    id: 'healthcare',
    name: 'Healthcare & Medical',
    duplicate_window_days: 45,
    requires_tcpa_consent: true,
    lifecycle_statuses: ['new','contacted','appointment_offered','appointment_confirmed','appointment_attended','no_show','lost'],
    intake_schema: {
      required: ['specialty_sought','urgency'],
      fields: {
        specialty_sought: { type: 'enum', options: ['general_practice','dentistry','physiotherapy','dermatology','orthopedics','mental_health','ophthalmology','gynecology','cardiology','other'] },
        appointment_type: { type: 'enum', options: ['first_consultation','follow_up','second_opinion','preventive'] },
        insurance_status: { type: 'enum', options: ['insured','uninsured','prefer_not_to_say'] },
        preferred_language: { type: 'string' },
        availability: { type: 'enum', options: ['mornings','afternoons','evenings','weekends','any'] },
        urgency: { type: 'enum', options: ['emergency','within_1_week','within_1_month','flexible'] },
        tcpa_consent: { type: 'boolean' },
        tcpa_consent_text: { type: 'string' },
        tcpa_consent_at: { type: 'timestamp' }
      }
    },
    active: true,
    created_at: '2024-01-01T00:00:00Z'
  },
  {
    id: 'loans',
    name: 'Loans',
    duplicate_window_days: 30,
    requires_tcpa_consent: true,
    lifecycle_statuses: ['new','contacted','application_started','application_submitted','approved','declined','lost'],
    intake_schema: {
      required: ['loan_type','loan_amount_min','credit_score_range'],
      fields: {
        loan_type: { type: 'enum', options: ['personal','mortgage','business','auto','student','debt_consolidation','home_equity','other'] },
        loan_amount_min: { type: 'number' },
        loan_amount_max: { type: 'number' },
        currency: { type: 'enum', options: ['EUR','USD'] },
        credit_score_range: { type: 'enum', options: ['excellent_750+','good_700_749','fair_650_699','poor_580_649','unknown'] },
        employment_status: { type: 'enum', options: ['employed_fulltime','employed_parttime','self_employed','retired','unemployed','other'] },
        purpose: { type: 'string' },
        existing_debt: { type: 'enum', options: ['none','manageable','struggling'] },
        tcpa_consent: { type: 'boolean' },
        tcpa_consent_text: { type: 'string' },
        tcpa_consent_at: { type: 'timestamp' }
      }
    },
    active: true,
    created_at: '2024-01-01T00:00:00Z'
  }
];